import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, Droplets } from "lucide-react";

import { renderCanvas } from "@/components/ui/canvas";

const LOGO_URL = "/pt-logo.png?v=2";
const TRANSPORT_IMG = "/hero-main.jpg";
const CLEANING_IMG = "https://images.pexels.com/photos/6873176/pexels-photo-6873176.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const POWER_WASHING_IMG = "https://images.pexels.com/photos/5652626/pexels-photo-5652626.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LandingPage() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  useEffect(() => {
    const cleanup = renderCanvas(canvasRef.current);
    return cleanup;
  }, []);

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-slate-950" data-testid="landing-page">
      <canvas
        ref={canvasRef}
        id="canvas"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-60"
        data-testid="landing-canvas"
      />

      <header className="relative z-10 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-20">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-2xl text-white font-['Outfit'] tracking-tight">Platinum Touch</span>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 pb-12">
        <div className="text-center mb-10 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight mb-4">
            Platinum Touch
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-xl mx-auto font-medium">
            Power. Precision. <span className="animate-glimmer-silver">Platinum.</span>
          </p>
        </div>

        <div className="w-full max-w-7xl grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          <div
            onClick={() => navigate('/transport')}
            className="group relative rounded-2xl overflow-hidden cursor-pointer min-h-[340px] md:min-h-[420px] animate-fade-in stagger-1"
            data-testid="transport-card"
          >
            <img
              src={TRANSPORT_IMG}
              alt="Auto Transport"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent transition-all duration-500 group-hover:from-slate-900/98" />

            <div className="relative z-10 h-full flex flex-col justify-end p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#38B6FF]/20 backdrop-blur-sm flex items-center justify-center border border-[#38B6FF]/30 group-hover:bg-[#38B6FF]/30 transition-colors">
                  <Truck className="w-6 h-6 text-[#38B6FF]" />
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-2">
                Auto Transport
              </h2>
              <p className="text-slate-300 text-sm sm:text-base mb-5 max-w-sm">
                Professional vehicle transport between Lakewood, NJ and Orlando, FL for Pesach 2026
              </p>
              <div className="flex items-center gap-2 text-[#38B6FF] font-semibold group-hover:gap-3 transition-all duration-300">
                <span>Book Transport</span>
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </div>

          <div
            onClick={() => navigate('/cleaning')}
            className="group relative rounded-2xl overflow-hidden cursor-pointer min-h-[340px] md:min-h-[420px] animate-fade-in stagger-2"
            data-testid="cleaning-card"
          >
            <img
              src={CLEANING_IMG}
              alt="Car Cleaning"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent transition-all duration-500 group-hover:from-slate-900/98" />

            <div className="relative z-10 h-full flex flex-col justify-end p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 backdrop-blur-sm flex items-center justify-center border border-emerald-500/30 group-hover:bg-emerald-500/30 transition-colors">
                  <Sparkles className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-2">
                Car Cleaning
              </h2>
              <p className="text-slate-300 text-sm sm:text-base mb-5 max-w-sm">
                Professional residential cleaning and full detailing services for all vehicle types
              </p>
              <div className="flex items-center gap-2 text-emerald-400 font-semibold group-hover:gap-3 transition-all duration-300">
                <span>Book Cleaning</span>
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </div>

          <div
            onClick={() => navigate('/power-washing')}
            className="group relative rounded-2xl overflow-hidden cursor-pointer min-h-[340px] md:min-h-[420px] animate-fade-in stagger-3"
            data-testid="power-washing-card"
          >
            <img
              src={POWER_WASHING_IMG}
              alt="Power Washing"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent transition-all duration-500 group-hover:from-slate-900/98" />

            <div className="relative z-10 h-full flex flex-col justify-end p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 backdrop-blur-sm flex items-center justify-center border border-blue-500/30 group-hover:bg-blue-500/30 transition-colors">
                  <Droplets className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-2">
                Power Washing
              </h2>
              <p className="text-slate-300 text-sm sm:text-base mb-5 max-w-sm">
                Professional residential and commercial power washing for driveways, sidewalks, patios, decks, fences, siding, and more. Free estimates available.
              </p>
              <div className="flex items-center gap-2 text-blue-400 font-semibold group-hover:gap-3 transition-all duration-300">
                <span>Get a Free Quote</span>
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 py-10 text-center" data-testid="landing-footer">
        <div className="mb-6 space-y-3">
          <p className="text-2xl sm:text-3xl font-bold text-white tracking-wide">
            Phone: <a href="tel:732-732-4310" className="hover:text-slate-300 transition-colors">(732) 732-4310</a>
          </p>
          <p className="text-2xl sm:text-3xl font-bold text-white tracking-wide">
            Email: <a href="mailto:bookings@platinumtouchnj.com" className="hover:text-slate-300 transition-colors">bookings@platinumtouchnj.com</a>
          </p>
        </div>
        <p className="text-slate-600 text-sm" data-testid="landing-footer-copy">
          &copy; {new Date().getFullYear()} Platinum Touch. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
